import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Accordion from "../../../components/accordian";
import { Controller } from "react-hook-form";
import TextInput from "../../../components/inputs/TextInput";
import ComboboxInput from "../../../components/inputs/ComboboxInput";
import { useGetAllNationalities } from "../../../hooks/common/query";
import { mapToOptions } from "../../../lib/functions";
import DatePickerInput from "../../../components/inputs/DatePickerInput";
import { APP_ROUTES } from "../../../routes";
import { useLocation } from "react-router-dom";
import TextAreaInput from "../../../components/inputs/TextAreaInput";

interface RequestingDepartmentInformationProps {
    control: any;
    errors: any;
    isFieldsDisabled: boolean;
    cardValidityFrom: Date
}

const RequestingDepartmentInformation = ({
    control,
    errors,
    isFieldsDisabled,
    cardValidityFrom
}: RequestingDepartmentInformationProps) => {
    const { t, } = useTranslation();

    const location = useLocation();

    const [selectedKey, setSelectedKey] = useState<string>("1");

    const [nationalities, setNationalities] = useState<
        { labelEn: string; labelAr: string; value: any }[]
    >([]);

    const { data: allNationalities } = useGetAllNationalities();

    useEffect(() => {
        if (allNationalities) {
            const mappedNationalities = mapToOptions(
                "name",
                "name_Ar",
                "iso3",
                allNationalities
            );
            setNationalities(mappedNationalities);
        }
    }, [allNationalities]);

    return (
        <div className="bg-white z-50 p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">

            <Accordion
                id="1"
                selectedKey={selectedKey}
                onSelectionChange={setSelectedKey}
                title={
                    <h1 className="text-xl md:text-2xl flex flex-1 -mt-2 items-center">
                        {t("1")}. {t("requestingDepartmentInformation")}
                    </h1>
                }
            >
                <div className="flex flex-col gap-2 lg:gap-4">
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
                        <Controller
                            name="contractorEmployeeName"
                            control={control}
                            render={({ field: { onChange, name, value } }) => (
                                <TextInput
                                    label={t("contractorEmployeeName")}
                                    value={value}
                                    onChange={onChange}
                                    error={errors?.[name]?.message}
                                    required
                                    readOnly={isFieldsDisabled}
                                />
                            )}
                        />
                        <Controller
                            name="nationality"
                            control={control}
                            render={({ field: { name, onChange, value } }) => {
                                return (
                                    <ComboboxInput
                                        options={nationalities}
                                        onChange={onChange}
                                        value={
                                            nationalities?.find((item) => item.value === value)
                                                ?.labelEn ||
                                            ""
                                        }
                                        error={errors?.[name] && t("nationalityRequired")}
                                        label={t("nationality")}
                                        required
                                        readOnly={isFieldsDisabled}
                                    />
                                );
                            }}
                        />

                        <Controller
                            name="nationalId"
                            control={control}
                            render={({ field: { onChange, name, value } }) => (
                                <TextInput
                                    label={t("nationalID")}
                                    value={value}
                                    onChange={onChange}
                                    error={errors?.[name]?.message}
                                    required
                                    readOnly={isFieldsDisabled}
                                    isInfoToolTip
                                    infoToolTip={<p>{t("enterNationalId")}</p>}
                                />
                            )}
                        />

                        <Controller
                            name="nationalIdExpiryDate"
                            control={control}
                            render={({ field: { name, value, onChange } }) => (
                                <DatePickerInput
                                    label={t("nationalIDExpiryDate")}
                                    value={value}
                                    required
                                    onChange={onChange}
                                    error={errors?.[name]?.message}
                                    readOnly={isFieldsDisabled}
                                    minDate={
                                        location.pathname ===
                                            APP_ROUTES.ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES
                                            ? new Date()
                                            : undefined
                                    }
                                />
                            )}
                        />

                        <Controller
                            name="dob"
                            control={control}
                            render={({ field: { name, value, onChange } }) => (
                                <DatePickerInput
                                    label={t("dateOfBirth")}
                                    value={value}
                                    required
                                    onChange={onChange}
                                    error={errors?.[name]?.message}
                                    readOnly={isFieldsDisabled}
                                    maxDate={new Date()}
                                />
                            )}
                        />
                        <Controller
                            name="contractorsCompanyName"
                            control={control}
                            render={({ field: { onChange, name, value } }) => (
                                <TextInput
                                    label={t("contractorsCompanyName")}
                                    onChange={onChange}
                                    value={value}
                                    error={errors?.[name]?.message}
                                    required
                                    readOnly={isFieldsDisabled}
                                />
                            )}
                        />
                        <Controller
                            name="department"
                            control={control}
                            render={({ field: { onChange, name, value } }) => (
                                <TextInput
                                    label={t("department")}
                                    onChange={onChange}
                                    value={value}
                                    error={errors?.[name]?.message}
                                    required
                                    readOnly={isFieldsDisabled}
                                />
                            )}
                        />
                        <Controller
                            name="jobPosition"
                            control={control}
                            render={({ field: { onChange, name, value } }) => (
                                <TextInput
                                    label={t("jobPosition")}
                                    onChange={onChange}
                                    value={value}
                                    error={errors?.[name]?.message}
                                    required
                                    readOnly={isFieldsDisabled}
                                />
                            )}
                        />
                        <Controller
                            name="workStartDate"
                            control={control}
                            render={({ field: { name, value, onChange } }) => (
                                <DatePickerInput
                                    label={t("workStartDate")}
                                    value={value}
                                    required
                                    onChange={onChange}
                                    error={errors?.[name]?.message}
                                    readOnly={isFieldsDisabled}
                                />
                            )}
                        />


                        <Controller
                            name="contactNumber"
                            control={control}
                            render={({ field: { onChange, name, value } }) => (
                                <TextInput
                                    label={t("contactNumber")}
                                    onChange={onChange}
                                    value={value}
                                    error={errors?.[name]?.message}
                                    required
                                    readOnly={isFieldsDisabled}
                                />
                            )}
                        />
                        <Controller
                            name="cardValidityFrom"
                            control={control}
                            render={({ field: { name, value, onChange } }) => (
                                <DatePickerInput
                                    label={t("cardValidityFrom")}
                                    value={value}
                                    required
                                    onChange={onChange}
                                    error={errors?.[name]?.message}
                                    readOnly={isFieldsDisabled}
                                />
                            )}
                        />
                        <Controller
                            name="cardValidityUntil"
                            control={control}
                            render={({ field: { name, value, onChange } }) => (
                                <DatePickerInput
                                    label={t("cardValidityUntil")}
                                    value={value}
                                    required
                                    onChange={onChange}
                                    error={errors?.[name]?.message}
                                    readOnly={isFieldsDisabled}
                                    minDate={
                                        (() => {
                                            if (cardValidityFrom) {
                                                return new Date(cardValidityFrom);
                                            }
                                            return new Date();
                                        })()
                                    }
                                />
                            )}
                        />
                    </div>

                    <Controller
                        name="residentialAddress"
                        control={control}
                        render={({ field: { onChange, name, value } }) => (
                            <TextAreaInput
                                rows={2}
                                label={t("residentialAddress")}
                                onChange={onChange}
                                value={value}
                                error={errors?.[name]?.message}
                                required
                                readOnly={isFieldsDisabled}
                            />
                        )}
                    />
                </div>
            </Accordion>
        </div>
    )
}

export default RequestingDepartmentInformation
